import React, { useEffect, Suspense, lazy, Component } from "react";
import "@/App.css";
import "./i18n";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import { CompareProvider } from "./context/CompareContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { GeoProvider } from "./context/GeoContext";
import AuthCallback from "./components/AuthCallback";
import RequireAuth from "./components/RequireAuth";
import CompareBar from "./components/CompareBar";
import { Toaster as SonnerToaster } from "sonner";
import { AuctionSnipeListener } from "./components/AuctionSnipeListener";
import SiteProtection from "./components/SiteProtection";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { OfflineIndicator } from "./components/OfflineIndicator";
import ChatWidget from "./components/ChatWidget";
import MobileBottomTabBar from "./components/MobileBottomTabBar";

// Retry wrapper for lazy imports — auto-retries once on ChunkLoadError
function lazyRetry(importFn) {
  return lazy(() =>
    importFn().catch(() => {
      // Chunk failed — force reload from server (skip cache)
      return new Promise((resolve) => {
        const hasReloaded = sessionStorage.getItem('chunk_reload');
        if (!hasReloaded) {
          sessionStorage.setItem('chunk_reload', '1');
          window.location.reload();
        }
        // If already reloaded once, just retry the import
        resolve(importFn());
      });
    })
  );
}

// Error boundary — catches any remaining ChunkLoadError and auto-reloads
class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk') || error?.message?.includes('Unexpected token')) {
      return { hasError: true };
    }
    return { hasError: true };
  }

  componentDidCatch(error) {
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk') || error?.message?.includes('Unexpected token')) {
      const reloadCount = parseInt(sessionStorage.getItem('chunk_reload_count') || '0', 10);
      if (reloadCount < 2) {
        sessionStorage.setItem('chunk_reload_count', String(reloadCount + 1));
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', fontFamily: 'system-ui, sans-serif' }}>
          <div style={{ textAlign: 'center', maxWidth: 400, padding: 32 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Page Load Error</h2>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 24, lineHeight: 1.6 }}>
              A new version of GOPIKO is available. Please reload to continue.
            </p>
            <button
              onClick={() => { sessionStorage.removeItem('chunk_reload'); sessionStorage.removeItem('chunk_reload_count'); window.location.reload(); }}
              style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '12px 32px', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lazy-loaded pages — each becomes a separate JS chunk (with retry on failure)
const HomePage = lazyRetry(() => import("./pages/HomePage"));
const PostAdPage = lazyRetry(() => import("./pages/PostAdPage"));
const LoginPage = lazyRetry(() => import("./pages/LoginPage"));
const SignUpPage = lazyRetry(() => import("./pages/SignUpPage"));
import GoogleCallbackPage from "./pages/GoogleCallbackPage";
const CategoryLandingPage = lazyRetry(() => import("./pages/CategoryLandingPage"));
const CategoryPage = lazyRetry(() => import("./pages/CategoryPage"));
const ListingDetailPage = lazyRetry(() => import("./pages/ListingDetailPage"));
const ComparePage = lazyRetry(() => import("./pages/ComparePage"));
const SearchResultsPage = lazyRetry(() => import("./pages/SearchResultsPage"));
const BuyInCityPage = lazyRetry(() => import("./pages/BuyInCityPage"));
const UserDashboard = lazyRetry(() => import("./pages/UserDashboard"));
const AdminPanel = lazyRetry(() => import("./pages/AdminPanel"));
const PromoteListing = lazyRetry(() => import("./pages/PromoteListing"));
const PromoteAuctionPage = lazyRetry(() => import("./pages/PromoteAuctionPage"));
const PremiumAdPage = lazyRetry(() => import("./pages/PremiumAdPage"));
const PaymentSuccess = lazyRetry(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazyRetry(() => import("./pages/PaymentCancel"));
const PayPalReturnPage = lazyRetry(() => import("./pages/PayPalReturnPage"));
const PostAdSuccessPage = lazyRetry(() => import("./pages/PostAdSuccessPage"));
const PrivacyPolicyPage = lazyRetry(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazyRetry(() => import("./pages/TermsOfServicePage"));
const AboutPage = lazyRetry(() => import("./pages/AboutPage"));
const ContactPage = lazyRetry(() => import("./pages/ContactPage"));
const HowItWorksPage = lazyRetry(() => import("./pages/HowItWorksPage"));
const FAQPage = lazyRetry(() => import("./pages/FAQPage"));
const GuidePage = lazyRetry(() => import("./pages/GuidePage"));
const DealerPackagesPage = lazyRetry(() => import("./pages/DealerPackagesPage"));
const DealersDirectoryPage = lazyRetry(() => import("./pages/DealersDirectoryPage"));
const DealerStorefrontPage = lazyRetry(() => import("./pages/DealerStorefrontPage"));
const PricingPage = lazyRetry(() => import("./pages/PricingPage"));
const SubscriptionSuccessPage = lazyRetry(() => import("./pages/SubscriptionSuccessPage"));
const AdminResetPassword = lazyRetry(() => import("./pages/AdminResetPassword"));
const AdminMusicPage = lazyRetry(() => import("./pages/AdminMusicPage"));
const AdminModerationPage = lazyRetry(() => import("./pages/AdminModerationPage"));
const EmployerDashboard = lazyRetry(() => import("./pages/EmployerDashboard"));
const CategoryDashboard = lazyRetry(() => import("./pages/CategoryDashboard"));
const RenewListingPage = lazyRetry(() => import("./pages/RenewListingPage"));
const ForgotPasswordPage = lazyRetry(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyRetry(() => import("./pages/ResetPasswordPage"));
const AuctionsPage = lazyRetry(() => import("./pages/AuctionsPage"));
const AuctionDetailPage = lazyRetry(() => import("./pages/AuctionDetailPage"));
const CreateAuctionPage = lazyRetry(() => import("./pages/CreateAuctionPage"));
const PaymentReceiptPage = lazyRetry(() => import("./pages/PaymentReceiptPage"));
const CandidateSearchPage = lazyRetry(() => import("./pages/CandidateSearchPage"));
const CandidateProfilePage = lazyRetry(() => import("./pages/CandidateProfilePage"));
const ShortlistsPage = lazyRetry(() => import("./pages/ShortlistsPage"));
const CheckEmailPage = lazyRetry(() => import("./pages/CheckEmailPage"));
const VerifyEmailPage = lazyRetry(() => import("./pages/VerifyEmailPage"));
const ReelsPage = lazyRetry(() => import("./pages/ReelsPage"));
const ReelsDiscoverPage = lazyRetry(() => import("./pages/ReelsDiscoverPage"));
const CreateReelPage = lazyRetry(() => import("./pages/CreateReelPage"));
const ReelAnalyticsPage = lazyRetry(() => import("./pages/ReelAnalyticsPage"));
const ReelCollectionsPage = lazyRetry(() => import("./pages/ReelCollectionsPage").then(m => ({ default: m.ReelCollectionsPage })));
const ReelCollectionDetailPage = lazyRetry(() => import("./pages/ReelCollectionsPage").then(m => ({ default: m.ReelCollectionDetailPage })));
const ReelChallengesPage = lazyRetry(() => import("./pages/ReelChallengesPage").then(m => ({ default: m.ReelChallengesPage })));
const ReelChallengeDetailPage = lazyRetry(() => import("./pages/ReelChallengesPage").then(m => ({ default: m.ReelChallengeDetailPage })));
const SavedReelsPage = lazyRetry(() => import("./pages/SavedReelsPage"));
const WatchHistoryPage = lazyRetry(() => import("./pages/WatchHistoryPage"));
const SoundPage = lazyRetry(() => import("./pages/SoundPage"));
const BlogListPage = lazyRetry(() => import("./pages/BlogListPage"));
const BlogArticlePage = lazyRetry(() => import("./pages/BlogArticlePage"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-3 border-gray-200 border-t-red-600 rounded-full animate-spin" />
        <p className="text-sm text-gray-400 font-medium">Loading...</p>
      </div>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Clear chunk reload flags on successful navigation
    sessionStorage.removeItem('chunk_reload');
    sessionStorage.removeItem('chunk_reload_count');
  }, [pathname]);
  return null;
}

import useGAPageView from "./hooks/useGAPageView";
import usePageTracker from "./hooks/usePageTracker";

function AppRouter() {
  const location = useLocation();
  useGAPageView();
  usePageTracker();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/post-ad" element={<PostAdPage />} />
        <Route path="/post-ad/success/:id" element={<PostAdSuccessPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<LoginPage />} />
        <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/check-email" element={<CheckEmailPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/category/:categoryId" element={<CategoryLandingPage />} />
        <Route path="/category/:categoryId/results" element={<CategoryPage />} />
        <Route path="/ad/:adId" element={<ListingDetailPage />} />
        <Route path="/search" element={<SearchResultsPage />} />
        <Route path="/my-gopiko" element={<UserDashboard />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/promote/:listingId" element={<PromoteListing />} />
        <Route path="/promote-auction/:auctionId" element={<RequireAuth><PromoteAuctionPage /></RequireAuth>} />
        <Route path="/premium-ad" element={<PremiumAdPage />} />
        <Route path="/auctions" element={<AuctionsPage />} />
        <Route path="/auction/:id" element={<AuctionDetailPage />} />
        <Route path="/create-auction" element={<RequireAuth><CreateAuctionPage /></RequireAuth>} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/payment/receipt/:transactionId" element={<PaymentReceiptPage />} />
        <Route path="/payment/paypal-return" element={<PayPalReturnPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/guide" element={<GuidePage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/dealer-packages" element={<DealerPackagesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
        <Route path="/dealers" element={<DealersDirectoryPage />} />
        <Route path="/dealer/:dealerId" element={<DealerStorefrontPage />} />
        <Route path="/admin/reset-password" element={<AdminResetPassword />} />
        <Route path="/admin/music" element={<AdminMusicPage />} />
        <Route path="/admin/moderation" element={<AdminModerationPage />} />
        <Route path="/employer" element={<EmployerDashboard />} />
        <Route path="/candidates" element={<CandidateSearchPage />} />
        <Route path="/candidates/:id" element={<CandidateProfilePage />} />
        <Route path="/shortlists" element={<ShortlistsPage />} />
        <Route path="/shortlists/:id" element={<ShortlistsPage />} />
        <Route path="/dashboard/:category" element={<CategoryDashboard />} />
        <Route path="/renew/:listingId" element={<RenewListingPage />} />
        <Route path="/reels" element={<ReelsDiscoverPage />} />
        <Route path="/reels/watch" element={<ReelsPage />} />
        <Route path="/reels/create" element={<RequireAuth><CreateReelPage /></RequireAuth>} />
        <Route path="/reels/analytics" element={<RequireAuth><ReelAnalyticsPage /></RequireAuth>} />
        <Route path="/reels/saved" element={<RequireAuth><SavedReelsPage /></RequireAuth>} />
        <Route path="/reels/history" element={<RequireAuth><WatchHistoryPage /></RequireAuth>} />
        <Route path="/reels/sound/:name" element={<SoundPage />} />
        <Route path="/reels/collections" element={<ReelCollectionsPage />} />
        <Route path="/reels/collections/:colId" element={<ReelCollectionDetailPage />} />
        <Route path="/reels/challenges" element={<ReelChallengesPage />} />
        <Route path="/reels/challenges/:chId" element={<ReelChallengeDetailPage />} />
        <Route path="/buy/:category/:location" element={<BuyInCityPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  const { i18n } = useTranslation();
  const rtlLanguages = ['ar'];
  const isRTL = rtlLanguages.includes(i18n.language);

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language, isRTL]);

  return (
    <HelmetProvider>
      <ChunkErrorBoundary>
          <div className="App" dir={isRTL ? 'rtl' : 'ltr'}>
            <AuthProvider>
              <GeoProvider>
                <CurrencyProvider>
                  <CompareProvider>
                    <BrowserRouter>
                      <ScrollToTop />
                      <AppRouter />
                      <CompareBar />
                      <SonnerToaster position="top-right" richColors />
                      <PWAInstallPrompt />
                      <OfflineIndicator />
                      <AuctionSnipeListener />
                      <SiteProtection />
                      <ChatWidget />
                      <MobileBottomTabBar />
                    </BrowserRouter>
                  </CompareProvider>
                </CurrencyProvider>
              </GeoProvider>
            </AuthProvider>
          </div>
      </ChunkErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
